import React, {ReactNode} from 'react';

import {Trans} from '../../libs/shared/helpers/src/internationalization/react';

/**
 * Converts a passed in list of JSX elements or strings into a nicely formatted list. An example use case
 * would be to join Trans items and strings together into a nicely formatted list.
 *
 * @param items Items to join together
 * @param separator [Optional] Separator between the items. Default: ','
 * @param shouldShowFinalSeparator [Optional] Whether to show the final items separator. Default: 'and'
 * @returns ReactNode of joined items
 */
export function renderInlineList(
  items: ReadonlyArray<ReactNode | string>,
  separator = ',',
  shouldShowFinalSeparator = true,
): ReactNode {
  return items
    .map<ReactNode>((item) => {
      if (typeof item === 'string') {
        return <span key={item}>{item}</span>;
      }
      return item;
    })
    .reduce((prev, curr, index) => {
      // If we are at the first node, return it only
      if (!prev) {
        return [curr];
      }

      // If we are viewing the last item
      if (index === items.length - 1) {
        const returnPayload = [prev];

        // If we have more than 2 items, add an oxford comma
        if (items.length > 2 && shouldShowFinalSeparator) {
          returnPayload.push(<span key="oxford-comma">{separator}</span>);
        }

        return [...returnPayload, renderFinalSeparator(separator, shouldShowFinalSeparator), curr];
      }

      // Join together, using index there is nothing else to create a unique key from
      // eslint-disable-next-line react/no-array-index-key
      return [prev, <span key={`separator-${index}`}>{separator} </span>, curr];
    }, null);
}

const renderFinalSeparator = (separator: string, shouldShowFinalSeparator: boolean) => {
  if (shouldShowFinalSeparator) {
    return (
      <span key="array-to-separated-list-and">
        {' '}
        <Trans id="array-to-separated-list-and">and</Trans>{' '}
      </span>
    );
  }
  return <span key="final-separator">{separator} </span>;
};
